import { differenceInDays, differenceInMilliseconds, secondsToMilliseconds } from 'date-fns'
import { TFunction } from 'i18next'

import {
  hasProductClosedStatus,
  hasProductDormantStatus,
  hasSubmittedAppropriatenessTest,
  showApptestRiskScreen,
} from '@utils/AccountAccess/accountProductStatuses'
import { isOne } from '@utils/validations'

import { AccountDetailedDto } from '@model/AccountDetailedDto'
import { TestAnswersDto } from '@model/ClientTestAnswerDto'
import { TickmillProductType } from '@model/TickmillProductType'

export const APPROPRIATENESS_TEST_MAX_ATTEMPTS = 3
export const APPROPRIATENESS_TEST_MAX_RETAKE_DAYS = 365

const calculateRemainingWaitingTime = (
  account: AccountDetailedDto | undefined,
  data: TestAnswersDto | undefined
) => {
  if (!account || !data) {
    return 0
  }

  const coolDownPeriod = account?.visibilityConfiguration?.appropriatenessTestCooldownPeriod
  const coolDownPeriodMs = secondsToMilliseconds(coolDownPeriod)

  const dateTimeNow = new Date().getTime()
  const dateTaken = new Date(data?.dateTaken).getTime()

  return coolDownPeriodMs - differenceInMilliseconds(dateTimeNow, dateTaken)
}

export interface GetRemainingWaitingTimeValue {
  days: number
  hours: number
  minutes: number
  seconds: number
}

export const getRemainingWaitingTime = (
  account: AccountDetailedDto | undefined,
  data: TestAnswersDto | undefined
): GetRemainingWaitingTimeValue => {
  const remainingWaitingTime = calculateRemainingWaitingTime(account, data)
  const time = remainingWaitingTime > 0 ? remainingWaitingTime : 0

  const days = Math.floor(time / (24 * 60 * 60 * 1000))
  const daysMs = time % (24 * 60 * 60 * 1000)

  const hours = Math.floor(daysMs / (60 * 60 * 1000))
  const hoursMs = daysMs % (60 * 60 * 1000)

  const minutes = Math.floor(hoursMs / (60 * 1000))
  const minutesMs = hoursMs % (60 * 1000)

  const seconds = Math.floor(minutesMs / 1000)

  return {
    days: days || 0,
    hours: hours || 0,
    minutes: minutes || 0,
    seconds: seconds || 0,
  }
}

export const getAppropriatenessTestRemainingRetakeDays = (
  data: TestAnswersDto | undefined
): number => {
  const dateTaken = data?.dateTaken
  if (!dateTaken) return APPROPRIATENESS_TEST_MAX_RETAKE_DAYS

  const diff = differenceInDays(new Date(), new Date(dateTaken))
  const remaining = APPROPRIATENESS_TEST_MAX_RETAKE_DAYS - diff
  return remaining > 0 ? remaining : 0
}

export const getDueDateMessage = (
  t: TFunction,
  attempt: number | undefined,
  remainingWaitingTime: GetRemainingWaitingTimeValue
): string => {
  const safeAttempt = attempt ?? 1
  const { days, hours, minutes, seconds } = remainingWaitingTime
  const isTimeLeft = !!(days + hours + minutes + seconds)

  if (isTimeLeft) {
    return t(
      'Profile.Your next appropriateness test (attempts {{attempt}} of 3) is due in {{days}} days {{hours}} hours {{minutes}} minutes {{seconds}} seconds.',
      {
        attempt: safeAttempt,
        days,
        hours,
        minutes,
        seconds,
      }
    )
  }

  if (isOne(safeAttempt)) {
    return t('Profile.You can start your appropriateness test (attempt {{attempt}} of 3).', {
      attempt: safeAttempt,
    })
  }

  return t('Profile.Your next appropriateness test (attempts {{attempt}} of 3)', {
    attempt: safeAttempt,
  })
}

export const canTakeTest = (
  accountDetailed: AccountDetailedDto | undefined,
  data: TestAnswersDto | undefined,
  product: TickmillProductType
): boolean => {
  if (!accountDetailed) return false
  if (showApptestRiskScreen(accountDetailed, product)) return false
  if (!data) return true
  if (hasProductDormantStatus(accountDetailed, product)) return true
  if (hasProductClosedStatus(accountDetailed, product)) return true
  if (!hasSubmittedAppropriatenessTest(accountDetailed, product)) return true

  return calculateRemainingWaitingTime(accountDetailed, data) <= 0
}
